/* eslint-disable no-param-reassign */
import _ from 'underscore';
import $ from 'jquery';

import { uncheckedInjectionContext as globalContext } from '@bingads-webui-campaign/injection-context';
import { DataSourceStore } from '@bingads-webui-campaign/data-source-store';
import { helpers } from '@bingads-webui/projection-grid-toolbar';
import { AccountUtils } from '@bingads-webui-clientcenter/entity-utils';
import { showLabelsPopup, filterOnLabelClick, navigateToManagePage } from '@bingads-webui-campaign/labels';
import { validateOrder } from '@bingads-webui-campaign/summary-grid-helper';

import * as CommonUtils from './common-utils';
import { gridsMap } from './constants';
import { applyCustomerHierarchyOrderbyFilter, HiddenCustomerHierarchyColumns, HiddenManagementColumns, ManualHoldStatusColumns } from './helper/customer-hierarchy-helper';
import { loadShowScheduledImportPopup, showImportSummaryDetailsPopup } from './helper/import-status-helper';
import { columnConfiguration } from './columns/index';
import { dummyDataSource } from '../mock/dummy-data-source';

const i18n = globalContext.require('@bingads-webui/i18n-amd-loader!campaignui/feature/accounts-summary');
const preferenceService = globalContext.requireDefault('component/preference/service/index');
const { showQualityScoreDetail } = globalContext.require('campaignui/feature/common-grid-config/column/quality-score/quality-score-click-handler');
const AlertPopup = globalContext.requireDefault('campaignui/feature/alerts-popup/view/alertsPopup');
const sortableHeaderTemplate = globalContext.require('campaignui/feature/common-grid-config/column/sortableHeaderTemplate');
const globalConfig = globalContext.require('component/config/index');
const jsdataCampaign = globalContext.require('component/js-data-campaign/index');
const edm = globalContext.requireDefault('component/edm-campaign/index');
const logger = globalContext.require('module/service/instrumentation/index');
const { legacyRouter } = globalContext.require('component/legacy-router/index');
const router = _.clone(legacyRouter);

const {
  deletedReasonValueMatcher: isDeletedMatcher,
  isDeleted: isAccountDeleted,
} = AccountUtils;
const {
  isAccountItemRow,
  isInImportTab,
  isInManagementOrBudgetTab,
  getGridPermissions,
  getSwitchCustomerShellLink,
} = CommonUtils;

const {
  IsCustomerHierarchyEnabled,
  IsManagementDashboardEnabled,
  IsAccountSummaryGridDataActionEnabled,
  IsCustomColumnsInAccountsSummarySupported,
  IsAccountDashboardAlertsColumnAllowed,
  IsInternalUser,
} = globalConfig.get('Permissions');

const headerRefreshCache = 'x-ms-refreshcampaigncache';
const headerLastWriteTime = 'x-ms-lastwritetime';
const isOdataActionSource = IsAccountSummaryGridDataActionEnabled || IsCustomColumnsInAccountsSummarySupported;
let alertPopup;
let alertPopupManagerAccounts;

const overridePreferenceConfig = ({ gridType, preferenceConfig }) => {
  const { orderby: orderbyInfo } = preferenceConfig.dataSource;
  const isImportGrid = isInImportTab(gridType);
  const config = _.extend({}, preferenceConfig);
  if (orderbyInfo && orderbyInfo.length > 0) {
    config.dataSource.orderby = applyCustomerHierarchyOrderbyFilter(orderbyInfo, IsCustomerHierarchyEnabled || IsManagementDashboardEnabled);
  }
  if (isImportGrid) {
    config.dataSource.query.$select = 'Name,AccountNumber,Id,OwnerId,AccountInactiveReasons,AccountInactiveReasonsCount,IsAccountAuthorized,IsParentAuthorized,AccountType,LastImportInfo';
  }

  const permissions = globalConfig.get('Permissions');
  if (permissions.IsAccountLabelsEnabled) {
    const labelsColumn = 'Labels';
    config.dataSource.query.$expand =
      config.dataSource.query.$expand ? config.dataSource.query.$expand += `,${labelsColumn}` : labelsColumn;
  }
  return config;
};

const afterFindAll = (resources, attrs, options = {}, queryBuilder, gridName) => {
  // Only for first call add these headers. After that cache manager will handle it
  delete options.useDfmCache;

  if (options.headers) {
    delete options.headers[headerRefreshCache];
    delete options.headers[headerLastWriteTime];
  }

  // Only for first call that was using the preload.
  // After that, we should let cache manager and x-ms-lastwritetime values decide which cache to use/bypass.
  if (queryBuilder) {
    const gridDataParameters = queryBuilder.getDecoratorByName('gridDataParameters');
    if (gridDataParameters && gridDataParameters.preloadOptions === 'UsePreload') {
      gridDataParameters.preloadOptions = undefined; // or 'None' should be similar
    }
  }

  if (gridName === 'PerformanceGrid' && Array.isArray(attrs) && attrs.length) {
    attrs.forEach((v) => {
      if (v.AvailableInsightTypeIds && v.AvailableInsightTypeIds.length) {
        logger.logMessage([logger.constants.LOGCATEGORY.TRACE], logger.constants.LOGLEVEL.INFO, '/accounts/performance', `Fluctuations Rendered. FromScenario: PerformanceGrid, TypeIds: ${v.AvailableInsightTypeIds.toString()}, aid: ${v.Id}}.`);
      }
    });
  }

  $.advertiser.clearErrorSummary();

  if (_.isArray(attrs)) {
    return attrs;
  }

  const errors = attrs ? attrs.errors : [];

  if (_.some(errors, error => error.Code === 'TooMuchDataToDownload')) {
    $.advertiser.appendErrorSummary(i18n.getString('Accounts_TooMuchDataToDownload'));
  } else {
    $.advertiser.appendErrorSummary(i18n.getString('Manage_Error_Default'));
  }

  return {};
};

const isAccountSelectable = ({ account, gridPermissions }) => {
  // Don't show checkbox for total row
  if (!isAccountItemRow(account)) {
    return false;
  }

  const isAccountActive = !isAccountDeleted(account);

  return _.some([
    gridPermissions.checkAndTransferAccount && isAccountActive,
    gridPermissions.checkAndUnlinkAccount && isAccountActive,
    gridPermissions.checkAndReactivateAccount && !isAccountActive,
    gridPermissions.checkAndDeleteAccount && isAccountActive,
    gridPermissions.checkAndEditAccount && isAccountActive,
    gridPermissions.importFromGoogleAds && isAccountActive,
  ]);
};

export function getConfig({
  view,
  gridType,
  preferenceConfig,
  viewport,
  filterService,
  isForVNext,
  preexecuteFlag = false,
  hasUnifiedSmartAccount = false,
  additionalEvents = {},
  customHeaderOffset,
  permissions,
  i18n: newI18n,
}) {
  const { gridName } = gridsMap[gridType];
  const preferenceConfigOverriden = overridePreferenceConfig({ gridType, preferenceConfig });
  const isManagementGrid = isInManagementOrBudgetTab(gridType);
  const isImportGrid = isInImportTab(gridType);

  // For the Old UI grid, this is set in the ui-legacy bootstrap. For the vNext grid, this is only set if DFM is enabled on the performance page.
  let customDataSource = DataSourceStore.get(gridName);

  const gridPermissions = getGridPermissions({ gridType });

  const { dynamic } = permissions;
  const { AccountSummaryFilterImprovementEnabled } = dynamic;

  if (customDataSource) {
    if (AccountSummaryFilterImprovementEnabled) {
      const { dataSource: cachedDataSource } = customDataSource;
      customDataSource = cachedDataSource;
    }
    // eslint-disable-next-line no-underscore-dangle
    customDataSource._options.afterFindAll = (resources, attrs) => afterFindAll(resources, attrs, customDataSource._options, customDataSource.queryBuilder, gridName);
    if (isForVNext) {
      customDataSource.queryBuilder.getDecoratorByName('filter').filterService = filterService;
    }
  }

  const entitySetParam = {
    gridDataParameters: {},
  };

  if (IsManagementDashboardEnabled) {
    entitySetParam.isCustomerHierachyRequest = true; // MT has a typo in this param, missing an r in 'hierarchy'
    if (isManagementGrid) {
      entitySetParam.isManagementRequest = true;
    }
  }

  // Mangement grid requires extra fields used by buttons/menus/checkbox in toolbar
  let extraColumns = null;

  if (IsCustomerHierarchyEnabled) {
    extraColumns = HiddenCustomerHierarchyColumns;
  } else if (IsManagementDashboardEnabled) {
    // field "OwnerId" gets assigned to special value so require extra field "Owner" In NewUI-Only management grid.
    extraColumns = HiddenManagementColumns;
  }

  extraColumns = [
    ...extraColumns,
    ...ManualHoldStatusColumns,
  ];

  // Account Mode filtering for Unified Product Phase 2
  // Note: should enable DFM for smart performance eventually, if DFM is turned on the grid call will not actually be made here
  // (see fetch-performance-page-data-webpack.js for how to set right param for DFM call)
  if (hasUnifiedSmartAccount) {
    if (CommonUtils.isInPerformanceTab(gridType) || CommonUtils.isInImportTab(gridType)) {
      entitySetParam.accountModes = ['Expert'];
    } else if (CommonUtils.isInSmartPerformanceTab(gridType)) {
      entitySetParam.accountModes = ['Smart'];
    }
  }

  const tableName = i18n.getString(gridsMap[gridType].tableNameLocalizedKey);

  const gridConfigCol = columnConfiguration({
    gridType, isForVNext, hasUnifiedSmartAccount, i18n: newI18n,
  });

  validateOrder(preferenceConfigOverriden, gridConfigCol, gridName, permissions);

  const gridConfig = {
    name: gridName,
    tableClasses: _.union(['table', 'table-bordered'], isImportGrid && ['bulk-status-grid']),
    tableAttributes: { 'aria-label': tableName },
    isDataFetchManagerSource: preexecuteFlag || !!customDataSource,
    filterService: isForVNext ? filterService : undefined,
    customerHierarchyExtras: extraColumns,
    dataSource: preexecuteFlag ? dummyDataSource : customDataSource || {
      type: 'jsdata',
      entity: jsdataCampaign.Account,
      primaryKey: 'Id',
      query: preferenceConfigOverriden.dataSource.query,
      orderby: preferenceConfigOverriden.dataSource.orderby,
      options: {
        cacheResponse: false,
        entitySet: isOdataActionSource ? edm.currentCustomer.Accounts['Default.GridData'].$call(entitySetParam) : edm.currentCustomer.Accounts,
        headers: {
          [headerRefreshCache]: true,
        },
        // DFM Cache is query-based and blocks calls that only update payload (e.g. segmentation type)
        useDfmCache: false,
        afterFindAll: (resources, attrs) => afterFindAll(resources, attrs, view.gridView.get('dataSource').options, undefined, gridName),
      },
    },
    query: customDataSource && {
      query: preferenceConfigOverriden.dataSource.query,
      orderby: preferenceConfigOverriden.dataSource.orderby,
    },
    sortableHeader: _.defaults(_.pick(preferenceConfigOverriden.sortableHeader, 'name', 'directionIndex'), {
      template: sortableHeaderTemplate,
    }),
    rows: {
      headRows: helpers.singleToolbarHeadRows(),
      bodyRows: [
        {
          type: 'data-rows',
          classes: {
            'deleted-account': item => isAccountDeleted(item),
          },
        },
      ],
    },
    scrolling: {
      header: {
        type: 'sticky',
        offset: customHeaderOffset || (() => $('nav.navbar-default.navbar-fixed-top').height()),
        attributes: { 'aria-label': i18n.getString('{{tableName}} table header', { tableName }) },
      },
      virtualized: true,
    },
    i18n,
    preferenceService,
    config: globalConfig,
    columns: gridConfigCol,
    events: _.pick({
      'click .account-inactive-reasons-cell .status_icon': IsAccountDashboardAlertsColumnAllowed && !isForVNext && ((e) => {
        const key = view.gridView.keyOfElement(e.target);
        const account = view.gridView.itemWithKey(key);

        $.gridPopup.clickEvent({ target: e.target }, () => {
          if (!alertPopup) {
            alertPopup = new AlertPopup({
              el: $(`#${AlertPopup.popupId}`),
            });
          }
          alertPopup.accountInactiveReasons = _.reject(account.AccountInactiveReasons, isDeletedMatcher);
          alertPopup.accountId = key;
          alertPopup.render();
        }, AlertPopup.popupId, '', true);
      }),
      'click .quality-score-cell .status-image.status_icon.gridDetailsImage': (e) => {
        const key = view.gridView.keyOfElement(e.target);
        const account = view.gridView.itemWithKey(key);

        showQualityScoreDetail(e, account.QualityScoreData, 'qsContainer');
      },
      'click .manager-accounts-cell .status_icon': !isForVNext && ((e) => {
        const key = view.gridView.keyOfElement(e.target);
        const account = view.gridView.itemWithKey(key);

        $.gridPopup.clickEvent({ target: e.target }, () => {
          if (!alertPopupManagerAccounts) {
            alertPopupManagerAccounts = new AlertPopup({
              el: $(`#${AlertPopup.popupId}`),
            });
          }
          alertPopupManagerAccounts.manageraccounts = _.map(account.ManagerAccounts, (managerAccount = {}) => ({
            ...managerAccount,
            link: IsInternalUser ? getSwitchCustomerShellLink(managerAccount.Id) : '',
          }));
          alertPopupManagerAccounts.renderManagerAccountsContent();
        }, AlertPopup.popupId, '', true);
      }),
      'click .import-summary.icon-info': !isForVNext && ((e) => {
        const key = view.gridView.keyOfElement(e.target);
        const account = view.gridView.itemWithKey(key);

        showImportSummaryDetailsPopup(e, account, i18n);
      }),
      'click .show-scheduled-imports': !isForVNext && ((e) => {
        loadShowScheduledImportPopup()
          .then(({ showScheduledImportPopup }) => {
            const key = view.gridView.keyOfElement(e.target);
            const account = view.gridView.itemWithKey(key);
            showScheduledImportPopup(e, account, $('.scheduled-imports-popup'));
          });
      }),
      'click .labels-cell-container a': ((event) => {
        const key = view.gridView.keyOfElement(event.target);
        const targetEntity = view.gridView.itemWithKey(key);

        showLabelsPopup({
          labels: targetEntity.Labels,
          entityType: 'Account',
          entityName: targetEntity.Name,
          event,
          router,
          i18n,
          grid: view,
          filterService,
          isMCCLabelEnabled: true,
        });
      }),
      'click .labels-cell-item': ((event) => {
        const key = view.gridView.keyOfElement(event.target);
        const targetEntity = view.gridView.itemWithKey(key);

        filterOnLabelClick(event, targetEntity.Labels, view.gridView, filterService, true);
      }),
      'click .manage-labels-button': () => {
        navigateToManagePage(router);
      },
      ...additionalEvents,
    }, Boolean),
    ...(isForVNext ? {
      selection: gridPermissions.checkboxColumns ? {
        selectable: account => isAccountSelectable({ account, gridPermissions }),
      } : undefined,
    } : {
      selection: gridPermissions.checkboxColumns ? {
        bodyClasses: ['text-center'],
        headClasses: ['text-center'],
        selectable: account => isAccountSelectable({ account, gridPermissions }),
      } : undefined,
      customDepsMap: (nameToDepsMap) => {
        if (nameToDepsMap && nameToDepsMap.filterState) {
          _.extend(nameToDepsMap, { filterState: _.union(nameToDepsMap.filterState, ['aggregatedRows']) });
        }
      },
    }),
  };

  if (viewport) {
    gridConfig.scrolling.viewport = viewport;
  }

  return gridConfig;
}
